import React, { Component } from 'react';
import { compose } from 'redux';
import moment from 'moment';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { FormattedMessage, FormattedHTMLMessage } from 'translations';
import { FormattedMessage as MzFormattedMessage } from 'mz-intl'
import RoundedHeader from 'app/components/base/RoundedHeader';
import InteractiveInfoBlock from 'app/components/base/InteractiveInfoBlock';
import { Layout, Flex } from 'app/components/base/Layout';
import {
  BOOK_TRIP_FORM,
  DEPARTING_TRIP_FIELD_NAME,
  RETURN_TRIP_FIELD_NAME,
  DEPARTING_PICKUP_FIELD_NAME,
  RETURNING_PICKUP_FIELD_NAME,
  RETURN_BOOK_STATE_FIELD_NAME,
  DEPARTING_FLIGHT_FIELD_NAME,
  RETURNING_FLIGHT_FIELD_NAME,
  DEPARTING_CRUISE_FIELD_NAME,
  DEPARTING_SHIP_FIELD_NAME,
  RETURN_TRIP_BOOKED,
  ROUND_TRIP_MODE_VALUE,
  TRIP_PICKUP_TIMES_ORIGIN_TZ_FIELD_NAME,
  SELECTED_TICKET_FIELD_NAME,
  SELECTED_RETURN_TICKET_FIELD_NAME,
  TRAIN_INFO_FIELD_NAME,
  RETURN_TRAIN_INFO_FIELD_NAME
} from 'app/constants';
import * as bookTripActions from 'app/actions/book-trip';
import * as scheduledRidesActions from 'app/actions/scheduled-rides';
import { preprocessTripObject } from 'app/utils/trip';
import {
  getTicketInfo
} from 'app/components/search-results/TimeTableSelect/utils/getTripScheduleProps';
import { MONTH_DAY_FORMAT, HOUR_FORMAT } from 'app/utils/types';
import TripDirectionLabel from './commons/TripDirectionLabel';
import TranslatedField from 'app/components/common/TranslatedField';
import PickupTimeline from 'mz-ui-kit/view/PickupTimeline';
import FlightField from 'mz-ui-kit/form/FlightField';
import TrainField from './commons/TrainFieldWithErrors';
import CN from 'classnames';
import Alert from 'mz-ui-kit/core/Alert'
import PortIcon from 'mz-icons/port'
import CalendarIcon from 'mz-icons/calendar'
import AirplaneIcon from 'mz-icons/airplane-outlined'
import TrainIcon from 'mz-icons/train'
import TicketsIcon from 'mz-icons/tickets-outlined'
import { PickupTimelineWrapper, FlightPickupAlertWrapper } from './elements';
import mzMessages from '../messages.intl'
import config from 'config';

import style from './style.css';


// Constants
const getCodeDirection = (fromLocation, toLocation, type) => {
  if (fromLocation?.type === type) {
    return {
      code: fromLocation.iataCode,
      direction: 'arr'
    }
  } else if (toLocation?.type === type) {
    return {
      code: toLocation.iataCode,
      direction: 'dep'
    }
  }
  return null
}

export const selector = formValueSelector(BOOK_TRIP_FORM);

export const mapStateToProps = state => {
  const returnBookState = selector(state, RETURN_BOOK_STATE_FIELD_NAME);

  const trip = selector(state, DEPARTING_TRIP_FIELD_NAME);
  const preparedTrip = preprocessTripObject(trip);

  const props = {
    trip,
    isRoundTrip: returnBookState === RETURN_TRIP_BOOKED,
    departingCruise: selector(state, DEPARTING_CRUISE_FIELD_NAME),
    pickupTimeChangedInfo: state.bookTrip.pickupTimeChangedInfo,
    selectedTicket: selector(state, SELECTED_TICKET_FIELD_NAME)
  };

  const returnTrip = selector(state, RETURN_TRIP_FIELD_NAME);
  const preparedReturnTrip = preprocessTripObject(returnTrip, ROUND_TRIP_MODE_VALUE);

  const tripData = {};
  if (preparedTrip) {
    const ignoreFlightInfo = preparedTrip.details.ignoreFlightInfo;
    const pickupDates = selector(state, TRIP_PICKUP_TIMES_ORIGIN_TZ_FIELD_NAME);

    Object.assign(tripData, {
      ignoreFlightInfo,
      waitTime: preparedTrip.details.waitTime,
      isScheduledTrip: preparedTrip.details.scheduledTrip,
      isHourlyRide: preparedTrip.details.isHourlyRide,
      departingFlightDatetime: trip && trip.flightDatetime,
      departingPickupDatetime: pickupDates[DEPARTING_PICKUP_FIELD_NAME],
      departingSearchId: trip && trip.searchId,
      providerNames: preparedTrip.heading.providerNames,
      returningPickupDatetime: pickupDates[RETURNING_PICKUP_FIELD_NAME],
      returningFlightDatetime: returnTrip && returnTrip.returnFlightDatetime,
      returningSearchId: returnTrip && returnTrip.searchId,
      selectedReturnTicket: selector(state, SELECTED_RETURN_TICKET_FIELD_NAME),
      flightFieldInfo: selector(state, DEPARTING_FLIGHT_FIELD_NAME),
      returningFlightFieldInfo: selector(state, RETURNING_FLIGHT_FIELD_NAME),
      returnTrip
    });

    if (preparedTrip.routing.fromPort || preparedTrip.routing.toPort) {
      Object.assign(tripData, {
        fromPort: preparedTrip.routing.fromPort,
        toPort: preparedTrip.routing.toPort
      });
      if (preparedReturnTrip) {
        Object.assign(tripData, {
          returnFromPort: preparedReturnTrip.routing.fromPort,
          returnToPort: preparedReturnTrip.routing.toPort
        });
      }
    }
    Object.assign(tripData, {
      fromLocation: preparedTrip.routing.fromLocation,
      toLocation: preparedTrip.routing.toLocation,
      trainStationName: preparedTrip.routing.trainStationName,
      fromIataCode: preparedTrip.routing.fromIataCode,
      toIataCode: preparedTrip.routing.toIataCode
    });
    if (preparedReturnTrip) {
      Object.assign(tripData, {
        returnFromLocation: preparedTrip.routing.fromLocation,
        returnToLocation: preparedTrip.routing.toLocation,
        returnTrainStationName: preparedReturnTrip.routing.trainStationName,
        returnFromIataCode: preparedReturnTrip.routing.fromIataCode,
        returnToIataCode: preparedReturnTrip.routing.toIataCode
      });
    }
  }

  return {
    ...props,
    ...tripData,
    flightLandingPickup: !!trip?.fltSupport && !!tripData.fromIataCode,
    returnFlightLandingPickup: !!trip?.fltSupport && !!tripData.returnFromIataCode,
  };
};

const mapDispatchToProps = {
  scheduleDepartureTime: scheduledRidesActions.scheduleDepartureTime,
  updateSchedule: scheduledRidesActions.updateSchedule,
  hidePickupTimeChangedNotification: bookTripActions.hidePickupTimeChangedNotification
};

export const messages = {
  trainInformation: { id: 'BOOK_TRIP.TRAIN_INFORMATION_TITLE' },
  titlePickup: { id: 'BOOK_TRIP.PICKUP_INFORMATION_TITLE' },
  titlePickupInformation: { id: 'BOOK_TRIP.FLIGHT_PICKUP_INFORMATION_TITLE' },
  cruiseInformationTitle: { id: 'BOOK_TRIP.CRUISE_INFORMATION_TITLE' },
  boardingInformation: { id: 'BOOK_TRIP.BOARDING_INFORMATION_TITLE' },
  flightsOptionHint: { id: 'BOOK_TRIP.FLIGHT.CANT_FIND' },
  mobileTitlePickupInformation: { id: 'BOOK_TRIP.MOBILE_FLIGHT_PICKUP_INFORMATION_TITLE' },
  pickupTimeChangedTitle: { id: 'BOOK_TRIP.PICKUP_TIME_CHANGED_NOTIFICATION.TITLE' },
  pickupTimeChangedMessage: { id: 'BOOK_TRIP.PICKUP_TIME_CHANGED_NOTIFICATION.MESSAGE' },
  ticketValidityMessage: { id: 'BOOK_TRIP.PICKUP_TIME_TICKET_VALIDITY' },
  rideDuration: { id: 'SEARCH_FORM.RIDE_DURATION' }
};

/**
 * Complete fully functional "Flight and Pickup" block builder that composes
 * all subcomponents together. It is a builder for mobule and desktop
 * versions of the block.
 * @param  {Component} DateTimeComponent
 * @return {Component}
 */
export const builder = ({
  CruiseComponent,
  ShipSelector,
  DateTimeComponent,
}) => {
  class PickupInformation extends Component {

    getTimeFieldParams = (fieldName, trip, selectedTicket, isReturn) => {
      if (!trip) return null;
      const { scheduleDepartureTime, updateSchedule } = this.props;

      const ticketInfo = getTicketInfo(trip, selectedTicket);

      const timeFieldParams = {
        name: fieldName,
        notime: true,
        disableFold: true,
        isReturn,
        trip,
        selectedTicket,
        scheduleDepartureTime,
        updateSchedule,
        forceEnabled: true,
        options: [],
        template: () => {
          if (!ticketInfo) return '';
          return `${ticketInfo.departureDatetime.format(HOUR_FORMAT)} | ${ticketInfo.ticketName}`;
        }
      };

      return timeFieldParams;
    }

    timeChangedNotification = ({ originTime, newTime }) => {
      const { hidePickupTimeChangedNotification } = this.props;
      return (
        <InteractiveInfoBlock
          iconClassName={style.timeChangedIcon}
          type="blue"
          icon={{ name: 'time-change', family: 'mozio' }}
          title={<FormattedMessage {...messages.pickupTimeChangedTitle} />}
          text={<FormattedHTMLMessage
            {...messages.pickupTimeChangedMessage}
            values={{ originTime, newTime }}
          />}
          onClickClose={hidePickupTimeChangedNotification}
        />
      );
    }

    ticketValidityMessage = date => {
      if (!date) return null;
      const { providerNames } = this.props;
      const message = (
        <FormattedHTMLMessage
          {...messages.ticketValidityMessage}
          values={{ provider: providerNames, date: date.format(MONTH_DAY_FORMAT) }}
        />
      );
      return message;
    }

    renderDepartureFlightSelector = () => {
      const {
        ignoreFlightInfo,
        fromLocation,
        toLocation,
        departingPickupDatetime,
        departingFlightDatetime,
        flightLandingPickup,
        departingSearchId
      } = this.props;

      const codeDir = getCodeDirection(fromLocation, toLocation, 'airport')
      if (!codeDir || ignoreFlightInfo || !departingPickupDatetime) return null;

      const requestContext = {
        airportCode: codeDir.code,
        flightDirection: codeDir.direction,
        pickupDatetime: departingPickupDatetime && departingPickupDatetime.format(),
        flightDatetime: departingFlightDatetime && departingFlightDatetime.format()
      };

      return (
        <Flex size="noshrink" className={style.flight}>
          <TripDirectionLabel
            airportName={codeDir.code}
            direction={requestContext.flightDirection}
          />
          <TranslatedField
            format={null}
            component={FlightField}
            name={DEPARTING_FLIGHT_FIELD_NAME}
            flightInfo={requestContext}
            searchId={departingSearchId}
          />
          {flightLandingPickup && fromLocation?.type === 'airport' && (
            this.renderFlightLandingPickupAlert()
          )}
        </Flex>
      );
    }

    renderFlightLandingPickupAlert() {
      return (
        <FlightPickupAlertWrapper>
          <Alert><MzFormattedMessage message={mzMessages.FLIGHT_LANDING_PICKUP_NOTICE} /></Alert>
        </FlightPickupAlertWrapper>
      )
    }

    renderDepartureCruiseSelector = () => {
      const { fromPort, toPort, ignoreFlightInfo } = this.props;
      // TODO: This is a temporary hot fix, we should move this logic to BE once we revamp checkout page
      if ((!fromPort && !toPort) || (ignoreFlightInfo && !config.REQUIRED_CRUISE_SHIP_NAME)) return null;

      return (
        <Flex size="noshrink">
          <CruiseComponent
            name={DEPARTING_CRUISE_FIELD_NAME}
            direction={fromPort ? 'fromPort' : 'toPort'}
            portName={fromPort || toPort}
            className={CN(style.field, style.airline)}
          />
        </Flex>
      );
    }

    renderDepartureShipSelector = () => {
      const { fromPort, toPort, ignoreFlightInfo } = this.props;
      // TODO: This is a temporary hot fix, we should move this logic to BE once we revamp checkout page
      if ((!fromPort && !toPort) || (ignoreFlightInfo && !config.REQUIRED_CRUISE_SHIP_NAME)) return null;

      return (
        <Flex className={style.field} size="noshrink">
          <ShipSelector
            name={DEPARTING_SHIP_FIELD_NAME}
            className={style.flight}
          />
        </Flex>
      );
    }

    renderDepartureDateTime = () => {
      const {
        ignoreFlightInfo,
        departingPickupDatetime,
        pickupTimeChangedInfo,
        selectedTicket,
        trip,
        isScheduledTrip,
        flightLandingPickup,
        fromIataCode,
        flightFieldInfo
      } = this.props;

      const hasFlightField = !!(!ignoreFlightInfo && departingPickupDatetime && fromIataCode);
      const notificationInfo = pickupTimeChangedInfo[DEPARTING_PICKUP_FIELD_NAME];
      const timeFieldParams = this.getTimeFieldParams(
        SELECTED_TICKET_FIELD_NAME,
        trip,
        selectedTicket
      );

      if (
        flightLandingPickup && (
          !flightFieldInfo || (
            hasFlightField &&
            flightFieldInfo?.flightDatetime &&
            flightFieldInfo?.origin
          )
        )
      ) {
        return null
      }

      return (
        <Flex size="noshrink">
          <DateTimeComponent
            type="departing"
            direction={fromIataCode ? 'fromAirport' : 'toAirport'}
            className={style.field}
            timeProps={isScheduledTrip && timeFieldParams}
            showRelativePicker={hasFlightField}
            flightTimeSelector={flightLandingPickup}
          />
          {notificationInfo && (
            this.timeChangedNotification(notificationInfo)
          )}
        </Flex>
      );
    }

    renderReturnFlightSelector = () => {
      const {
        ignoreFlightInfo,
        returnFromLocation,
        returnToLocation,
        returningPickupDatetime,
        returningFlightDatetime,
        returningSearchId,
        returnFlightLandingPickup,
      } = this.props;

      const codeDir = getCodeDirection(returnToLocation, returnFromLocation, 'airport')
      if (!codeDir || ignoreFlightInfo || !returningPickupDatetime) return null

      const requestContext = {
        returnVoyage: true,
        airportCode: codeDir.code,
        flightDirection: codeDir.direction,
        pickupDatetime: returningPickupDatetime && returningPickupDatetime.format(),
        flightDatetime: returningFlightDatetime && returningFlightDatetime.format()
      };

      return (
        <Flex size="noshrink" className={CN(style.flight, style.return)}>
          <TripDirectionLabel
            airportName={codeDir.code}
            direction={requestContext.flightDirection}
            returning
          />
          <TranslatedField
            format={null}
            component={FlightField}
            name={RETURNING_FLIGHT_FIELD_NAME}
            flightInfo={requestContext}
            searchId={returningSearchId}
          />
          {returnFlightLandingPickup && returnToLocation?.type === 'airport' && (
            this.renderFlightLandingPickupAlert()
          )}
        </Flex>
      );
    }

    renderReturnDateTime = () => {
      const {
        isRoundTrip,
        pickupTimeChangedInfo,
        returningPickupDatetime,
        ignoreFlightInfo,
        selectedReturnTicket,
        returnTrip,
        isScheduledTrip,
        returnFromIataCode,
        returnFlightLandingPickup,
        returningFlightFieldInfo,
      } = this.props;
      if (!isRoundTrip) return null;

      const hasFlightField = !!(!ignoreFlightInfo && returningPickupDatetime && returnFromIataCode);
      const notificationInfo = pickupTimeChangedInfo[RETURNING_PICKUP_FIELD_NAME];
      const timeFieldParams = this.getTimeFieldParams(
        SELECTED_RETURN_TICKET_FIELD_NAME,
        returnTrip,
        selectedReturnTicket,
        true
      );

      if (
        returnFlightLandingPickup && (
          !returningFlightFieldInfo || (
            hasFlightField &&
            returningFlightFieldInfo?.flightDatetime &&
            returningFlightFieldInfo?.origin
          )
        )
      ) {
        return null;
      }

      return (
        <Flex size="noshrink">
          <DateTimeComponent
            type="returning"
            direction={returnFromIataCode ? 'fromAirport' : 'toAirport'}
            className={style.field}
            timeProps={isScheduledTrip && timeFieldParams}
            showRelativePicker={hasFlightField}
            flightTimeSelector={returnFlightLandingPickup}
          />
          {notificationInfo && (
            this.timeChangedNotification(notificationInfo)
          )}
        </Flex>
      );
    }

    renderPickupTimeline = () => {
      const {
        fromLocation,
        toLocation,
        departingPickupDatetime,
        flightFieldInfo,
        flightLandingPickup,
        waitTime
      } = this.props;

      const codeDir = getCodeDirection(fromLocation, toLocation, 'airport')
      if (!codeDir
        || codeDir.direction !== 'arr'
        || !flightFieldInfo
        || !flightFieldInfo.flightDatetime
        || !config.SHOW_BTP_PICKUP_TIMELINE
        || (fromLocation?.type === 'airport' && flightLandingPickup)
      ) {
        return null;
      }

      return (
        <PickupTimelineWrapper>
          <PickupTimeline
            pickupPoint={codeDir.code}
            landingTime={moment.parseZone(flightFieldInfo.flightDatetime)}
            driverArrivesTime={departingPickupDatetime}
            freeWaitingMinutes={waitTime}
          />
        </PickupTimelineWrapper>
      );
    }

    renderReturnPickupTimeline = () => {
      const {
        returnFromLocation,
        returnToLocation,
        returningPickupDatetime,
        returningFlightFieldInfo,
        returnFlightLandingPickup,
        waitTime
      } = this.props;

      const codeDir = getCodeDirection(returnToLocation, returnFromLocation, 'airport')
      if (!codeDir
          || codeDir.direction !== 'arr'
          || !returningFlightFieldInfo
          || !returningFlightFieldInfo.flightDatetime
          || !config.SHOW_BTP_PICKUP_TIMELINE
          || (returnToLocation?.type === 'airport' && returnFlightLandingPickup)
        ) {
        return null;
      }

      return (
        <PickupTimelineWrapper>
          <PickupTimeline
            appearance="return"
            pickupPoint={codeDir.code}
            landingTime={moment.parseZone(
              returningFlightFieldInfo.flightDatetime
            )}
            driverArrivesTime={returningPickupDatetime}
            freeWaitingMinutes={waitTime}
          />
        </PickupTimelineWrapper>
      );
    }

    renderTrainSelector() {
      const {
        departingPickupDatetime,
        trainStationName,
        fromLocation,
        toLocation,
        ignoreFlightInfo,
      } = this.props;

      const codeDir = getCodeDirection(fromLocation, toLocation, 'train_station')
      if (!codeDir || !trainStationName || ignoreFlightInfo) return;

      const trainInfo = {
        pickupDatetime: departingPickupDatetime,
        stationCode: codeDir.code,
        trainDirection: codeDir.direction,
      }

      return (
        <div className={style.field}>
          <TripDirectionLabel
            stationName={trainStationName}
            direction={trainInfo.trainDirection}
          />
          <TranslatedField
            component={TrainField}
            name={TRAIN_INFO_FIELD_NAME}
            trainInfo={trainInfo}
          />
        </div>
      )
    }

    renderReturnTrainSelector() {
      const {
        returningPickupDatetime,
        returnTrainStationName,
        returnFromLocation,
        returnToLocation,
        ignoreFlightInfo,
      } = this.props;

      const codeDir = getCodeDirection(returnToLocation, returnFromLocation, 'train_station')
      if (!codeDir || !returnTrainStationName || ignoreFlightInfo) return;

      const trainInfo = {
        pickupDatetime: returningPickupDatetime,
        stationCode: codeDir.code,
        trainDirection: codeDir.direction,
      }

      return (
        <div className={CN(style.field, style.return)}>
          <TripDirectionLabel
            stationName={returnTrainStationName}
            direction={trainInfo.trainDirection}
            returning
          />
          <TranslatedField
            component={TrainField}
            name={RETURN_TRAIN_INFO_FIELD_NAME}
            trainInfo={trainInfo}
          />
        </div>
      )
    }

    render() {
      const {
        fromLocation,
        toLocation,
        ignoreFlightInfo,
        isScheduledTrip,
      } = this.props;

      let HedaerIcon = CalendarIcon
      let tripTypeMessage = messages.titlePickup;
      const locationTypes = new Set([
        fromLocation?.type,
        toLocation?.type
      ])

      if (isScheduledTrip) {
        HedaerIcon = TicketsIcon
        tripTypeMessage = messages.boardingInformation;
      } else if (
        !ignoreFlightInfo &&
        locationTypes.has('train_station') &&
        (locationTypes.has('place') || locationTypes.size === 1)
      ) {
        HedaerIcon = TrainIcon
        tripTypeMessage = messages.trainInformation;
      } else if (
        !ignoreFlightInfo &&
        locationTypes.has('airport') &&
        (locationTypes.has('place') || locationTypes.size === 1)
      ) {
        HedaerIcon = AirplaneIcon
        tripTypeMessage = messages.mobileTitlePickupInformation;
      } else if (
        !ignoreFlightInfo &&
        locationTypes.has('port') &&
        (locationTypes.has('place') || locationTypes.size === 1)
      ) {
        HedaerIcon = PortIcon
        tripTypeMessage = messages.cruiseInformationTitle;
      }

      return (
        <Layout direction="column" nowrap className={this.props.className}>
          <Flex size="noshrink" className={style.title}>
            <RoundedHeader icon={<HedaerIcon />}>
              <FormattedMessage {...tripTypeMessage} />
            </RoundedHeader>
          </Flex>

          {this.renderDepartureCruiseSelector()}
          {this.renderDepartureShipSelector()}
          {config.SHOW_TRAIN_SELECTION && this.renderTrainSelector()}
          {this.renderDepartureFlightSelector()}
          {this.renderDepartureDateTime()}
          {this.renderPickupTimeline()}
          {config.SHOW_TRAIN_SELECTION && this.renderReturnTrainSelector()}
          {this.renderReturnFlightSelector()}
          {this.renderReturnDateTime()}
          {this.renderReturnPickupTimeline()}
        </Layout>
      );
    }
  }

  // TODO: move to Flow types
  // PickupInformation.propTypes = {
  //   className: PropTypes.string,
  //   trip: PropTypes.object,
  //   isScheduledTrip: PropTypes.bool,
  //   returnTrip: PropTypes.object,
  //   isRoundTrip: PropTypes.bool,
  //   departingAirline: PropTypes.object,
  //   departingPickupDatetime: PropTypes.object,
  //   returningAirline: PropTypes.object,
  //   returningPickupDatetime: PropTypes.object,
  //   ignoreFlightInfo: PropTypes.bool,
  //   fromPort: PropTypes.string,
  //   toPort: PropTypes.string,
  //   returnFromPort: PropTypes.string,
  //   returnToPort: PropTypes.string,
  //   fromIataCode: PropTypes.string,
  //   toIataCode: PropTypes.string,
  //   returnFromIataCode: PropTypes.string,
  //   returnToIataCode: PropTypes.string,
  //   pickupTimeChangedInfo: PropTypes.object,
  //   providerNames: PropTypes.bool,
  //   selectedTicket: PropTypes.object,
  //   selectedReturnTicket: PropTypes.object,

  //   // actions
  //   scheduleDepartureTime: PropTypes.func.isRequired,
  //   updateSchedule: PropTypes.func.isRequired,
  //   hidePickupTimeChangedNotification: PropTypes.func.isRequired
  // };

  return compose(
    connect(mapStateToProps, mapDispatchToProps)
  )(PickupInformation);
};

export default builder;
